import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { extractBatchPage, type ExtractedExcerptNode } from './pageTreeUtils.ts';
import PageTreeMacroExcerptBatchQuery from './queries/__generated__/PageTreeMacroExcerptBatchQuery.graphql.ts';
import type {
	PageTreeMacroExcerptBatchQuery$data,
	PageTreeMacroExcerptBatchQuery$variables,
} from './queries/__generated__/PageTreeMacroExcerptBatchQuery.graphql.ts';

/** Server-side cap for `content(ids:)` and aligned cursor page size. */
const EXCERPT_BATCH_LIMIT = 25;

export type ExcerptEntry = {
	adfString?: string;
	mediaToken?: string;
};

export type UseExcerptBatchLoaderResult = {
	/**
	 * Progressively populated map of excerpt entries keyed by content ID. A
	 * missing key means either "not requested yet" or "in flight". A present
	 * key means the batch loader has written a result for that ID (entry may
	 * still be empty if the server returned no excerpt for that ID).
	 */
	excerptMap: ReadonlyMap<string, ExcerptEntry>;
	/**
	 * Enqueue IDs for batched excerpt fetching. Idempotent across calls --
	 * already-seen IDs are deduped. Stable reference across renders.
	 */
	enqueueExcerptIds: (ids: readonly string[]) => void;
};

/**
 * One "request slot" -- the variables for the next batch query the
 * declarative `useQueryInterop` should fire.
 */
type RequestSlot = {
	ids: string[];
	after: string | null;
};

type LoaderState = {
	excerptMap: ReadonlyMap<string, ExcerptEntry>;
	currentRequest: RequestSlot | null;
};

const INITIAL_STATE: LoaderState = { excerptMap: new Map(), currentRequest: null };

/** Stable variables passed to `useQueryInterop` while skipped. */
const SKIP_VARIABLES: PageTreeMacroExcerptBatchQuery$variables = {
	ids: [],
	first: EXCERPT_BATCH_LIMIT,
	after: null,
};

/**
 * Build a Map containing the previous entries plus newly-resolved nodes.
 * Extracted into a separate function so the per-node loop is not lexically
 * nested inside the per-page loop in the post-fetch effect -- the
 * `@atlassian/perf-linting/no-quadratic-complexity` rule flags nested
 * iteration even when the inner loop is bounded.
 */
const mergeBatchPage = (
	prev: ReadonlyMap<string, ExcerptEntry>,
	nodes: readonly ExtractedExcerptNode[],
): Map<string, ExcerptEntry> => {
	const next = new Map(prev);
	for (const node of nodes) {
		if (!node.id) continue;
		next.set(node.id, { adfString: node.adfString, mediaToken: node.mediaToken });
	}
	return next;
};

/**
 * Excerpt batch loader.
 *
 * Drives the paginated batch query via the declarative `useQueryInterop`
 * hook -- matching the convention used by the rest of this package
 * (`PageTreeNode`'s children + excerpt queries). The "imperative loop"
 * shape is expressed as a state machine: when `currentRequest` is set,
 * `useQueryInterop` fires with those variables; when the result lands, a
 * single `setState` merges the page's nodes into `excerptMap` and advances
 * `currentRequest` to either the next page of the same chunk, the next
 * chunk, or `null` (idle).
 *
 */
export const useExcerptBatchLoader = (showExcerpt: boolean): UseExcerptBatchLoaderResult => {
	const [state, setState] = useState<LoaderState>(INITIAL_STATE);

	// Pending IDs queued for the next chunk. Mutated synchronously by
	// `enqueueExcerptIds`; consumed when a chunk completes (or when the loader is
	// idle and a new request comes in).
	const pendingQueueRef = useRef<string[]>([]);
	// IDs we've ever queued. Used to dedup so the same ID never makes it
	// into two batches.
	const seenIdsRef = useRef<Set<string>>(new Set());
	// Tracks which response object we've already processed so the post-fetch
	// effect doesn't re-process the same data on unrelated renders.
	const processedDataRef = useRef<PageTreeMacroExcerptBatchQuery$data | undefined>(undefined);

	const variables = useMemo<PageTreeMacroExcerptBatchQuery$variables>(
		() =>
			state.currentRequest
				? {
						ids: state.currentRequest.ids,
						first: EXCERPT_BATCH_LIMIT,
						after: state.currentRequest.after,
					}
				: SKIP_VARIABLES,
		[state.currentRequest],
	);

	const takeNextRequest = useCallback((): RequestSlot | null => {
		if (pendingQueueRef.current.length === 0) {
			return null;
		}
		const nextIds = pendingQueueRef.current.splice(0, EXCERPT_BATCH_LIMIT);
		return { ids: nextIds, after: null };
	}, []);

	const batchResult = useQueryInterop<
		PageTreeMacroExcerptBatchQuery$data,
		PageTreeMacroExcerptBatchQuery$variables
	>(PageTreeMacroExcerptBatchQuery, {
		variables,
		skip: !showExcerpt || state.currentRequest === null,
	});

	// Post-fetch handler: merges the resolved page into `excerptMap` AND
	// advances the request pointer in a single `setState`. Combining both
	// updates in one call sidesteps the
	// `@atlassian/perf-linting/no-chain-state-updates` rule.
	useEffect(() => {
		const request = state.currentRequest;
		if (!request) return;
		if (batchResult.loading) return;
		// Recovery path: if the query failed (or resolved without data), advance
		// to the next queued chunk so we don't deadlock `currentRequest` forever.
		if (batchResult.error || !batchResult.data) {
			setState((prev) => {
				if (prev.currentRequest !== request) return prev;
				return { ...prev, currentRequest: takeNextRequest() };
			});
			return;
		}
		const data = batchResult.data;
		if (data === processedDataRef.current) return;
		processedDataRef.current = data;

		const { nodes, pageInfo } = extractBatchPage(data);
		const hasNextPage = !!pageInfo?.hasNextPage;
		const nextCursor = pageInfo?.endCursor ?? null;
		const canPaginate = hasNextPage && !!nextCursor && nextCursor !== request.after;

		let nextRequest: RequestSlot | null;
		if (canPaginate) {
			nextRequest = { ids: request.ids, after: nextCursor };
		} else {
			nextRequest = takeNextRequest();
		}

		setState((prev) => ({
			excerptMap: nodes.length > 0 ? mergeBatchPage(prev.excerptMap, nodes) : prev.excerptMap,
			currentRequest: nextRequest,
		}));
	}, [
		batchResult.data,
		batchResult.error,
		batchResult.loading,
		state.currentRequest,
		takeNextRequest,
	]);

	const enqueueExcerptIds = useCallback(
		(ids: readonly string[]): void => {
			let added = false;
			for (const id of ids) {
				if (!id || seenIdsRef.current.has(id)) continue;
				seenIdsRef.current.add(id);
				pendingQueueRef.current.push(id);
				added = true;
			}
			if (!added) return;
			// Only kick off a fresh request if the loader is currently idle.
			// Otherwise the in-flight chunk will pull from the queue when it
			// finishes.
			setState((prev) => {
				if (prev.currentRequest !== null) return prev;
				const nextRequest = takeNextRequest();
				return nextRequest ? { ...prev, currentRequest: nextRequest } : prev;
			});
		},
		[takeNextRequest],
	);

	return { excerptMap: state.excerptMap, enqueueExcerptIds };
};
